import { create } from "zustand";

import type { ClipRef, Timeline, Track } from "../api/projects";

const empty: Timeline = {
  version: 1, fps: 30, width: 1920, height: 1080, background: "#000000",
  tracks: [
    { id: "v1", type: "video", clips: [] },
    { id: "a1", type: "audio", clips: [], volume: 1 },
  ],
  overlays: [],
};

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

/** Devuelve el clip + su track buscando por id de clip. */
function findClipAndTrack(
  timeline: Timeline, clipId: string,
): { clip: ClipRef; track: Track } | null {
  for (const t of timeline.tracks) {
    const c = t.clips.find((x) => x.id === clipId);
    if (c) return { clip: c, track: t };
  }
  return null;
}

interface ClipboardEntry {
  trackType: "video" | "audio";
  clip: ClipRef;
}

interface EditorState {
  projectId: number | null;
  timeline: Timeline;
  selectedClipIds: string[];
  playhead_ms: number;
  dirty: boolean;
  clipboard: ClipboardEntry[];

  setProject: (id: number, tl: Timeline) => void;
  setTimeline: (tl: Timeline) => void;

  // Mediateca → timeline
  addVideoClip: (clipId: number, durationMs: number, name: string) => void;
  addAudioClip: (clipId: number, durationMs: number, name: string) => void;
  addClipAt: (params: {
    mediaId: number;
    mediaType: "video" | "audio" | "image";
    durationMs: number;
    trackId?: string;
    startMs?: number;
  }) => void;

  // Manipulación de clips
  removeClip: (id: string) => void;
  removeSelected: () => void;
  moveClip: (id: string, newStart: number) => void;
  resizeClip: (id: string, side: "in" | "out", deltaMs: number) => void;
  setClipEffect: (id: string, effect: any) => void;
  addClipEffectByType: (clipId: string, type: "adjust" | "lut" | "blur") => void;
  updateClipEffect: (clipId: string, effectId: string, patch: any) => void;
  removeClipEffect: (clipId: string, effectId: string) => void;
  toggleClipEffect: (clipId: string, effectId: string) => void;
  setClipVolume: (clipId: string, volume: number) => void;
  splitClipAtTime: (clipId: string, timeMs: number) => void;
  splitSelectedOrUnderPlayhead: () => void;
  setClipTrack: (clipId: string, targetTrackId: string) => void;
  setTrackFlag: (trackId: string, key: "muted" | "solo" | "ducked", value: boolean) => void;

  // Text overlays
  selectedOverlayId: string | null;
  addTextOverlay: (atMs?: number) => void;
  updateOverlay: (id: string, patch: any) => void;
  removeOverlay: (id: string) => void;
  selectOverlay: (id: string | null) => void;

  // Gestión de pistas
  addTrack: (type: "video" | "audio") => void;
  removeTrack: (trackId: string) => void;
  renameTrack: (trackId: string, name: string) => void;
  reorderTrack: (trackId: string, direction: "up" | "down") => void;

  // Selección
  selectClip: (id: string | null) => void;        // mantiene API antigua
  selectOne: (id: string) => void;                // selección única
  toggleSelect: (id: string) => void;             // Ctrl+click
  addToSelection: (id: string) => void;           // Shift+click
  selectMany: (ids: string[]) => void;            // rubber band
  selectAll: () => void;
  clearSelection: () => void;

  // Portapapeles
  copySelection: () => void;
  cutSelection: () => void;
  pasteAtPlayhead: () => void;
  duplicateSelection: () => void;

  // Playhead
  setPlayhead: (ms: number) => void;

  markSaved: () => void;
  durationMs: () => number;
}

export const useEditor = create<EditorState>((set, get) => ({
  projectId: null,
  timeline: empty,
  selectedClipIds: [],
  playhead_ms: 0,
  dirty: false,
  clipboard: [],

  setProject: (id, tl) => {
    // Migración: cualquier efecto sin id recibe uno (timelines v1 antiguos).
    if (tl && tl.tracks) {
      for (const t of tl.tracks) {
        for (const c of t.clips) {
          if (c.effects) {
            for (const e of c.effects as any[]) {
              if (!e.id) e.id = uid();
              if (e.enabled === undefined) e.enabled = true;
            }
          }
        }
      }
    }
    set({
      projectId: id,
      timeline: tl && tl.tracks ? tl : empty,
      selectedClipIds: [],
      playhead_ms: 0,
      dirty: false,
    });
  },

  setTimeline: (tl) => set({ timeline: tl, dirty: true }),

  addVideoClip: (clipId, durationMs, _name) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    const v = tracks.find((t) => t.type === "video") as Track & { type: "video" };
    const lastEnd = v.clips.reduce((m, c) => Math.max(m, c.start + (c.out - c.in)), 0);
    const newClip: ClipRef = {
      id: uid(), clipId, in: 0, out: durationMs, start: lastEnd, effects: [],
    };
    v.clips.push(newClip);
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: [newClip.id] };
  }),

  addAudioClip: (clipId, durationMs, _name) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    const a = tracks.find((t) => t.type === "audio") as Track & { type: "audio" };
    const lastEnd = a.clips.reduce((m, c) => Math.max(m, c.start + (c.out - c.in)), 0);
    const newClip: ClipRef = { id: uid(), clipId, in: 0, out: durationMs, start: lastEnd };
    a.clips.push(newClip);
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: [newClip.id] };
  }),

  addClipAt: ({ mediaId, mediaType, durationMs, trackId, startMs }) => set((s) => {
    const wantedType: "video" | "audio" = mediaType === "audio" ? "audio" : "video";
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    // Resolver track destino: preferir el trackId solicitado si es del tipo correcto;
    // si no, primer track del tipo correcto.
    let target = trackId
      ? tracks.find((t) => t.id === trackId && t.type === wantedType)
      : undefined;
    if (!target) target = tracks.find((t) => t.type === wantedType);
    if (!target) return s;
    const startResolved = startMs !== undefined
      ? Math.max(0, startMs)
      : target.clips.reduce((m, c) => Math.max(m, c.start + (c.out - c.in)), 0);
    const newClip: ClipRef = {
      id: uid(), clipId: mediaId,
      in: 0, out: durationMs, start: startResolved,
      effects: mediaType === "audio" ? undefined : [],
    };
    target.clips.push(newClip);
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: [newClip.id] };
  }),

  removeClip: (id) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: t.clips.filter((c) => c.id !== id) }));
    return {
      timeline: { ...s.timeline, tracks }, dirty: true,
      selectedClipIds: s.selectedClipIds.filter((x) => x !== id),
    };
  }),

  removeSelected: () => set((s) => {
    if (s.selectedClipIds.length === 0) return s;
    const ids = new Set(s.selectedClipIds);
    const tracks = s.timeline.tracks.map((t) => ({
      ...t, clips: t.clips.filter((c) => !ids.has(c.id)),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: [] };
  }),

  moveClip: (id, newStart) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === id ? { ...c, start: Math.max(0, newStart) } : c),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  resizeClip: (id, side, deltaMs) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => {
        if (c.id !== id) return c;
        if (side === "in") {
          const ni = Math.max(0, Math.min(c.out - 100, c.in + deltaMs));
          return { ...c, in: ni, start: c.start + (ni - c.in) };
        }
        return { ...c, out: Math.max(c.in + 100, c.out + deltaMs) };
      }),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  setClipEffect: (id, effect) => set((s) => {
    // API antigua: reemplaza por tipo. Mantenida para compatibilidad.
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => {
        if (c.id !== id) return c;
        const others = (c.effects || []).filter((e) => e.type !== effect.type);
        const withId = { id: effect.id || uid(), enabled: true, ...effect };
        return { ...c, effects: [...others, withId] };
      }),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  addClipEffectByType: (clipId, type) => set((s) => {
    let defaults: any = { id: uid(), type, enabled: true };
    if (type === "adjust") {
      defaults = { ...defaults, exposure: 0, contrast: 1, saturation: 1, temperature: 0 };
    } else if (type === "lut") {
      defaults = { ...defaults, file: "warm.cube", intensity: 1 };
    } else if (type === "blur") {
      defaults = { ...defaults, radius: 5 };
    }
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId
        ? { ...c, effects: [...(c.effects || []), defaults] }
        : c
      ),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  updateClipEffect: (clipId, effectId, patch) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId
        ? { ...c, effects: (c.effects || []).map((e: any) =>
            e.id === effectId ? { ...e, ...patch } : e) }
        : c
      ),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  removeClipEffect: (clipId, effectId) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId
        ? { ...c, effects: (c.effects || []).filter((e: any) => e.id !== effectId) }
        : c
      ),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  toggleClipEffect: (clipId, effectId) => set((s) => {
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId
        ? { ...c, effects: (c.effects || []).map((e: any) =>
            e.id === effectId ? { ...e, enabled: e.enabled === false ? true : false } : e) }
        : c
      ),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  setClipVolume: (clipId, volume) => set((s) => {
    const v = Math.max(0, Math.min(2, volume));
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId ? { ...c, volume: v } : c),
    }));
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  setTrackFlag: (trackId, key, value) => set((s) => ({
    timeline: {
      ...s.timeline,
      tracks: s.timeline.tracks.map((t) =>
        t.id === trackId ? ({ ...t, [key]: value } as Track) : t),
    },
    dirty: true,
  })),

  selectedOverlayId: null,

  addTextOverlay: (atMs) => set((s) => {
    const start = atMs ?? s.playhead_ms;
    const newOverlay: any = {
      id: uid(),
      type: "text",
      text: "Tu texto aquí",
      start, end: start + 3000,
      x: 0.5, y: 0.85,
      font_size: 64,
      color: "#ffffff",
      bold: true,
      align: "center",
      background: "rgba(0,0,0,0.55)",
      padding: 12,
      fade_in_ms: 300, fade_out_ms: 300,
    };
    const overlays = [...(s.timeline.overlays || []), newOverlay];
    return {
      timeline: { ...s.timeline, overlays },
      dirty: true,
      selectedOverlayId: newOverlay.id,
      selectedClipIds: [],
    };
  }),

  updateOverlay: (id, patch) => set((s) => {
    const overlays = (s.timeline.overlays || []).map((o: any) =>
      o.id === id ? { ...o, ...patch } : o);
    return { timeline: { ...s.timeline, overlays }, dirty: true };
  }),

  removeOverlay: (id) => set((s) => ({
    timeline: {
      ...s.timeline,
      overlays: (s.timeline.overlays || []).filter((o: any) => o.id !== id),
    },
    dirty: true,
    selectedOverlayId: s.selectedOverlayId === id ? null : s.selectedOverlayId,
  })),

  selectOverlay: (id) => set({ selectedOverlayId: id, selectedClipIds: [] }),

  splitClipAtTime: (clipId, timeMs) => set((s) => {
    const found = findClipAndTrack(s.timeline, clipId);
    if (!found) return s;
    const { clip } = found;
    const clipEnd = clip.start + (clip.out - clip.in);
    if (timeMs <= clip.start + 50 || timeMs >= clipEnd - 50) return s; // demasiado cerca de un borde
    const splitInMs = clip.in + (timeMs - clip.start);
    const first: ClipRef = { ...clip, out: splitInMs };
    const second: ClipRef = {
      ...clip, id: uid(), in: splitInMs, start: timeMs,
      effects: clip.effects ? clip.effects.map((e) => ({ ...e })) : undefined,
      transitionOut: clip.transitionOut,
    };
    // El primer trozo pierde la transitionOut, el segundo la conserva
    first.transitionOut = undefined;
    const tracks = s.timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.flatMap((c) => c.id === clipId ? [first, second] : [c]),
    }));
    return {
      timeline: { ...s.timeline, tracks }, dirty: true,
      selectedClipIds: [first.id, second.id],
    };
  }),

  setClipTrack: (clipId, targetTrackId) => set((s) => {
    let moved: ClipRef | null = null;
    let sourceType: "video" | "audio" | null = null;
    const stripped = s.timeline.tracks.map((t) => {
      const idx = t.clips.findIndex((c) => c.id === clipId);
      if (idx >= 0 && !moved) {
        moved = t.clips[idx];
        sourceType = t.type;
        return { ...t, clips: t.clips.filter((c) => c.id !== clipId) };
      }
      return t;
    });
    if (!moved) return s;
    const target = stripped.find((t) => t.id === targetTrackId);
    if (!target) return s;
    if (target.type !== sourceType) return s;  // no permitimos vídeo↔audio
    const tracks = stripped.map((t) =>
      t.id === targetTrackId ? { ...t, clips: [...t.clips, moved!] } : t,
    );
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  addTrack: (type) => set((s) => {
    const existing = s.timeline.tracks.filter((t) => t.type === type);
    const n = existing.length + 1;
    const prefix = type === "video" ? "V" : "A";
    const id = `${prefix.toLowerCase()}${Date.now().toString(36).slice(-4)}`;
    const newTrack: Track = type === "video"
      ? { id, type: "video", name: `${prefix}${n}`, clips: [] }
      : { id, type: "audio", name: `${prefix}${n}`, clips: [], volume: 1 };
    return { timeline: { ...s.timeline, tracks: [...s.timeline.tracks, newTrack] }, dirty: true };
  }),

  removeTrack: (trackId) => set((s) => {
    const t = s.timeline.tracks.find((x) => x.id === trackId);
    if (!t) return s;
    // Si tiene clips, también los desselecciona
    const removedIds = new Set(t.clips.map((c) => c.id));
    return {
      timeline: { ...s.timeline, tracks: s.timeline.tracks.filter((x) => x.id !== trackId) },
      dirty: true,
      selectedClipIds: s.selectedClipIds.filter((id) => !removedIds.has(id)),
    };
  }),

  renameTrack: (trackId, name) => set((s) => ({
    timeline: {
      ...s.timeline,
      tracks: s.timeline.tracks.map((t) =>
        t.id === trackId ? ({ ...t, name } as Track) : t),
    },
    dirty: true,
  })),

  reorderTrack: (trackId, direction) => set((s) => {
    const idx = s.timeline.tracks.findIndex((t) => t.id === trackId);
    if (idx < 0) return s;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= s.timeline.tracks.length) return s;
    const tracks = [...s.timeline.tracks];
    [tracks[idx], tracks[newIdx]] = [tracks[newIdx], tracks[idx]];
    return { timeline: { ...s.timeline, tracks }, dirty: true };
  }),

  splitSelectedOrUnderPlayhead: () => {
    const s = get();
    const t = s.playhead_ms;
    // Si hay selección, divide solo esos clips si caen bajo el playhead
    let candidates: string[] = [];
    if (s.selectedClipIds.length) {
      candidates = s.selectedClipIds.filter((id) => {
        const found = findClipAndTrack(s.timeline, id);
        if (!found) return false;
        return t > found.clip.start + 50 && t < found.clip.start + (found.clip.out - found.clip.in) - 50;
      });
    }
    // Si no, todos los clips bajo el playhead
    if (!candidates.length) {
      for (const track of s.timeline.tracks) {
        for (const c of track.clips) {
          const end = c.start + (c.out - c.in);
          if (t > c.start + 50 && t < end - 50) candidates.push(c.id);
        }
      }
    }
    candidates.forEach((id) => get().splitClipAtTime(id, t));
  },

  selectClip: (id) => set({ selectedClipIds: id ? [id] : [], selectedOverlayId: null }),
  selectOne: (id) => set({ selectedClipIds: [id], selectedOverlayId: null }),
  toggleSelect: (id) => set((s) => ({
    selectedClipIds: s.selectedClipIds.includes(id)
      ? s.selectedClipIds.filter((x) => x !== id)
      : [...s.selectedClipIds, id],
    selectedOverlayId: null,
  })),
  addToSelection: (id) => set((s) => ({
    selectedClipIds: s.selectedClipIds.includes(id) ? s.selectedClipIds : [...s.selectedClipIds, id],
    selectedOverlayId: null,
  })),
  selectMany: (ids) => set({ selectedClipIds: Array.from(new Set(ids)), selectedOverlayId: null }),
  selectAll: () => set((s) => {
    const all: string[] = [];
    for (const t of s.timeline.tracks) for (const c of t.clips) all.push(c.id);
    return { selectedClipIds: all, selectedOverlayId: null };
  }),
  clearSelection: () => set({ selectedClipIds: [], selectedOverlayId: null }),

  copySelection: () => {
    const s = get();
    if (!s.selectedClipIds.length) return;
    const entries: ClipboardEntry[] = [];
    for (const id of s.selectedClipIds) {
      const found = findClipAndTrack(s.timeline, id);
      if (found) entries.push({ trackType: found.track.type, clip: { ...found.clip } });
    }
    set({ clipboard: entries });
  },

  cutSelection: () => {
    get().copySelection();
    get().removeSelected();
  },

  pasteAtPlayhead: () => set((s) => {
    if (!s.clipboard.length) return s;
    const baseStart = Math.min(...s.clipboard.map((e) => e.clip.start));
    const playhead = s.playhead_ms;
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    const newIds: string[] = [];
    for (const entry of s.clipboard) {
      const targetTrack = tracks.find((t) => t.type === entry.trackType);
      if (!targetTrack) continue;
      const nc: ClipRef = {
        ...entry.clip, id: uid(),
        start: playhead + (entry.clip.start - baseStart),
      };
      targetTrack.clips.push(nc);
      newIds.push(nc.id);
    }
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: newIds };
  }),

  duplicateSelection: () => set((s) => {
    if (!s.selectedClipIds.length) return s;
    const tracks = s.timeline.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    const newIds: string[] = [];
    for (const id of s.selectedClipIds) {
      for (const t of tracks) {
        const idx = t.clips.findIndex((c) => c.id === id);
        if (idx >= 0) {
          const original = t.clips[idx];
          const len = original.out - original.in;
          const nc: ClipRef = {
            ...original, id: uid(),
            start: original.start + len,
          };
          t.clips.push(nc);
          newIds.push(nc.id);
          break;
        }
      }
    }
    return { timeline: { ...s.timeline, tracks }, dirty: true, selectedClipIds: newIds };
  }),

  setPlayhead: (ms) => set({ playhead_ms: Math.max(0, ms) }),

  markSaved: () => set({ dirty: false }),

  durationMs: () => {
    const tl = get().timeline;
    let max = 0;
    for (const t of tl.tracks) {
      for (const c of t.clips) {
        const end = c.start + (c.out - c.in);
        if (end > max) max = end;
      }
    }
    return max;
  },
}));
